import { useCallback, useEffect, useId, useMemo, useState } from 'react'
import { isServer } from '@weberus/utils'
import { useParams, usePathname } from 'next/navigation'

type Hook = (param: { id: string }) => [boolean, (opened: boolean) => void]

const idMap: Record<string, boolean> = {}
const HASH_STACK_DELIMITER = '|'
const MODAL_HASH_HISTORY_STATE_KEY = '__useModalHashV2'

/**
 * 현재 URL의 해시 스택을 반환하는 함수
 */
const getCurrentHashStack = (): string[] => {
  if (typeof window === 'undefined') return []
  return window.location.hash
    .substring(1)
    .split(HASH_STACK_DELIMITER)
    .filter(v => !!v)
}

const isModalInHashStack = (modalId: string) => getCurrentHashStack().includes(modalId)

// hash stack 을 이용하여 url 을 생성
const getUrlWithHashStack = (hashStack: string[]) => {
  const hash = hashStack.filter(v => !!v).join(HASH_STACK_DELIMITER)
  return `${window.location.pathname}${window.location.search}${hash ? `#${hash}` : ''}`
}

const getHistoryStateWithUrl = (url: string) => {
  const state = window.history.state as unknown
  if (!state || typeof state !== 'object') return state

  const stateRecord = state as Record<string, unknown>
  const options = stateRecord.options
  const optionsRecord =
    options && typeof options === 'object' ? (options as Record<string, unknown>) : {}

  return {
    ...stateRecord,
    // Next router 는 popstate 처리 시 history.state 의 url/as 를 참조하므로
    // 주소창 URL 과 state URL 을 같이 갱신해야 hash stack 이 한 단계씩 복원된다.
    as: url,
    url,
    options: {
      ...optionsRecord,
      shallow: true,
      scroll: false,
    },
    [MODAL_HASH_HISTORY_STATE_KEY]: true,
  }
}

const isModalHashHistoryState = () => {
  const state = window.history.state as unknown
  return (
    !!state &&
    typeof state === 'object' &&
    (state as Record<string, unknown>)[MODAL_HASH_HISTORY_STATE_KEY] === true
  )
}

const openModalHash = (modalId: string) => {
  const hashStack = getCurrentHashStack()
  if (hashStack.includes(modalId)) return

  const nextUrl = getUrlWithHashStack([...hashStack, modalId])
  window.history.pushState(getHistoryStateWithUrl(nextUrl), '', nextUrl)
}

const closeModalHash = (modalId: string): boolean => {
  const hashStack = getCurrentHashStack()
  if (!hashStack.includes(modalId)) return true

  const isTopModal = hashStack[hashStack.length - 1] === modalId
  if (!isTopModal) return false

  // 이 hook 이 push 한 history entry 는 back 으로 제거해야 중복 entry 가 남지 않는다.
  if (hashStack.length > 1 || isModalHashHistoryState()) {
    window.history.back()
    return true
  }

  // 직접 hash URL 로 진입한 경우처럼 hook 이 만든 entry 가 아니면 페이지 이탈을 막기 위해 hash 만 제거한다.
  const nextUrl = getUrlWithHashStack([])
  window.history.replaceState(getHistoryStateWithUrl(nextUrl), '', nextUrl)
  return true
}

/**
 * Modal의 아이디에 해당하는 값을 해시뱅으로 이용하여 오픈 여부 결정
 * 앱에서 '뒤로가기' 처리를 위해 사용한다.
 * @param id {string} 모달에 해당하는 아이디
 */
const useModalHashV2: Hook = function ({ id }) {
  const pathname = usePathname()
  const params = useParams()

  const uniqueId = useId().replace(/:/g, '')
  const modalId = useMemo(() => `${id}-${uniqueId}`, [id, uniqueId])

  const [opened, setOpened] = useState<boolean>(() => {
    if (isServer()) {
      return false
    }
    return isModalInHashStack(modalId)
  })

  useEffect(() => {
    if (idMap[modalId] && process.env.NODE_ENV !== 'production') {
      console.warn(
        `[useModalHashV2] Modal ID 에 중복이 있습니다. 중복된 ID 는 "${modalId}" 입니다. 잘못 설정된 것이 아닌지 다시 확인해 주세요.`
      )
    }
    idMap[modalId] = true
    return () => void (idMap[modalId] = false)
  }, [modalId])

  useEffect(() => {
    const syncOpened = () => {
      setOpened(isModalInHashStack(modalId))
    }

    // 해시 변경을 감지해 모달 오픈 / 닫기를 수행한다.
    syncOpened()
    window.addEventListener('popstate', syncOpened)
    window.addEventListener('hashchange', syncOpened)

    return () => {
      window.removeEventListener('popstate', syncOpened)
      window.removeEventListener('hashchange', syncOpened)
    }
  }, [modalId, pathname, params])

  const setState = useCallback(
    (isOpened: boolean) => {
      if (isOpened) {
        openModalHash(modalId)
        setOpened(true)
        return
      }

      const closeModalHashResult = closeModalHash(modalId)
      if (closeModalHashResult) setOpened(false)
    },
    [modalId]
  )

  return [opened, setState]
}

export default useModalHashV2
